import React from 'react';
import './InventorySeriesReconciliationHistory.css';

const isRecord = value =>
	Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isPositiveInteger = value => Number.isInteger(value) && value > 0;
const isNullablePositiveInteger = value =>
	value === null || isPositiveInteger(value);
const isTimestamp = value =>
	typeof value === 'string' &&
	value.trim() !== '' &&
	!Number.isNaN(new Date(value).getTime());

const isActor = value =>
	isRecord(value) &&
	isPositiveInteger(value.userId) &&
	isPositiveInteger(value.entityId);

const isSnapshot = value =>
	isRecord(value) &&
	isPositiveInteger(value.warehouseItemId) &&
	isPositiveInteger(value.itemId) &&
	isPositiveInteger(value.warehouseId) &&
	isPositiveInteger(value.stateId) &&
	isNullablePositiveInteger(value.lastWtdId) &&
	isTimestamp(value.updatedAt);

const isExternalReference = value =>
	value === null ||
	(isRecord(value) &&
		typeof value.system === 'string' &&
		value.system.trim() !== '' &&
		typeof value.reference === 'string' &&
		value.reference.trim() !== '' &&
		(value.occurredAt === null || isTimestamp(value.occurredAt)));

export const getInventorySeriesReconciliationHistory = value => {
	if (
		!isRecord(value) ||
		value.version !== 1 ||
		!['requested', 'applied'].includes(value.status) ||
		value.reasonCode !== 'MISSING_EXTERNAL_RETURN' ||
		typeof value.reason !== 'string' ||
		!value.reason.trim() ||
		value.reason.trim().length > 1000 ||
		!isExternalReference(value.externalReference) ||
		!isSnapshot(value.before) ||
		!isActor(value.requestedBy) ||
		!isTimestamp(value.requestedAt) ||
		!isRecord(value.batch) ||
		!isPositiveInteger(value.batch.importId) ||
		!isPositiveInteger(value.batch.reviewImportId)
	)
		return null;

	if (
		value.status === 'applied' &&
		(!isSnapshot(value.after) ||
			!isActor(value.approvedBy) ||
			!isTimestamp(value.approvedAt))
	)
		return null;

	if (
		value.status === 'requested' &&
		(value.after !== undefined ||
			value.approvedBy !== undefined ||
			value.approvedAt !== undefined)
	)
		return null;

	return {
		status: value.status,
		reason: value.reason.trim(),
		externalReference: value.externalReference,
		before: value.before,
		after: value.status === 'applied' ? value.after : null,
		requestedBy: value.requestedBy,
		requestedAt: value.requestedAt,
		approvedBy: value.status === 'applied' ? value.approvedBy : null,
		approvedAt: value.status === 'applied' ? value.approvedAt : null,
	};
};

const AuditValue = ({ label, children }) => (
	<>
		<dt>{label}</dt>
		<dd>{children}</dd>
	</>
);

const Timestamp = ({ value }) =>
	value ? <time dateTime={value}>{value}</time> : 'Pendiente';

const Actor = ({ value }) =>
	value ? `Usuario #${value.userId} · Entidad #${value.entityId}` : 'Pendiente';

const Snapshot = ({ title, value }) => (
	<section
		className='inventory-series-reconciliation-snapshot'
		aria-label={title}
	>
		<h5>{title}</h5>
		{value ? (
			<dl>
				<AuditValue label='Almacén'>#{value.warehouseId}</AuditValue>
				<AuditValue label='Estado'>#{value.stateId}</AuditValue>
				<AuditValue label='Artículo'>#{value.itemId}</AuditValue>
				<AuditValue label='Artículo de almacén'>
					#{value.warehouseItemId}
				</AuditValue>
				<AuditValue label='Última transacción'>
					{value.lastWtdId ? `#${value.lastWtdId}` : '—'}
				</AuditValue>
				<AuditValue label='Actualizado'>
					<Timestamp value={value.updatedAt} />
				</AuditValue>
			</dl>
		) : (
			<p>Pendiente</p>
		)}
	</section>
);

const InventorySeriesReconciliationHistory = ({ value, color }) => {
	const audit = getInventorySeriesReconciliationHistory(value);
	if (!audit) return null;

	const isApplied = audit.status === 'applied';
	return (
		<section
			className='inventory-series-reconciliation-history'
			aria-label='Auditoría de reconciliación de inventario'
			style={color ? { color } : undefined}
		>
			<h4>
				Reconciliación de inventario{' '}
				{isApplied ? 'aplicada' : 'pendiente de aprobación'}
			</h4>
			<dl className='inventory-series-reconciliation-actors'>
				<AuditValue label='Razón'>{audit.reason}</AuditValue>
				<AuditValue label='Solicitante'>
					<Actor value={audit.requestedBy} />
				</AuditValue>
				<AuditValue label='Fecha de solicitud'>
					<Timestamp value={audit.requestedAt} />
				</AuditValue>
				<AuditValue label='Aprobador'>
					<Actor value={audit.approvedBy} />
				</AuditValue>
				<AuditValue label='Fecha de aprobación'>
					<Timestamp value={audit.approvedAt} />
				</AuditValue>
			</dl>

			<section aria-label='Referencia externa'>
				<h5>Referencia externa</h5>
				{audit.externalReference ? (
					<dl>
						<AuditValue label='Sistema'>
							{audit.externalReference.system}
						</AuditValue>
						<AuditValue label='Referencia'>
							{audit.externalReference.reference}
						</AuditValue>
						<AuditValue label='Fecha del documento'>
							<Timestamp value={audit.externalReference.occurredAt} />
						</AuditValue>
					</dl>
				) : (
					<p>Sin referencia externa</p>
				)}
			</section>

			<div className='inventory-series-reconciliation-snapshots'>
				<Snapshot title='Ubicación anterior' value={audit.before} />
				<Snapshot title='Ubicación posterior' value={audit.after} />
			</div>
		</section>
	);
};

export default InventorySeriesReconciliationHistory;
